import {
  getDefaultHotkeyKeys,
  HOTKEY_REGISTRY,
  isHotkeyId,
  type HotkeyId,
} from '@/lib/hotkeys/registry';

export type HotkeyOverrides = Record<string, string>;

export interface ApplyHotkeyOverrideResult {
  overrides: HotkeyOverrides;
  conflictId: HotkeyId | null;
}

/**
 * Compute the next sparse override map after setting/clearing one binding.
 * Pure helper so conflict + cleanup logic stays unit-tested and low-complexity
 * at the React hook boundary.
 */
export function applyHotkeyOverride(
  current: HotkeyOverrides,
  id: HotkeyId,
  keys: string | null,
): ApplyHotkeyOverrideResult {
  const nextKeys =
    keys === null || keys === getDefaultHotkeyKeys(id)
      ? getDefaultHotkeyKeys(id)
      : keys;

  // Conflict-check the resolved binding (including when restoring a default)
  // against every other currently-resolved shortcut.
  const conflictId = findConflictingHotkey(current, id, nextKeys);
  if (conflictId) {
    return { overrides: current, conflictId };
  }

  if (nextKeys === getDefaultHotkeyKeys(id)) {
    return { overrides: clearOverride(current, id), conflictId: null };
  }

  return {
    overrides: pruneUnknownOverrides({ ...current, [id]: nextKeys }),
    conflictId: null,
  };
}

export function resolveHotkeyKeys(
  overrides: HotkeyOverrides,
  id: HotkeyId,
): string {
  const override = overrides[id];
  if (typeof override === 'string' && override.length > 0) {
    return override;
  }
  return getDefaultHotkeyKeys(id);
}

/** Normalize for equality checks (order-independent modifiers). */
export function normalizeCombo(keys: string): string {
  return canonicalCombo(keys, 'literal');
}

/**
 * True when two stored combos fire the same physical keys on macOS or on
 * Windows/Linux. `mod` is Meta on Mac and Ctrl elsewhere, so a Mac-saved
 * `ctrl+k` collides with portable `mod+k` once that preference is used on
 * Windows. `meta` stays distinct from `mod` until that platform mapping.
 */
export function combosConflict(left: string, right: string): boolean {
  return (
    canonicalCombo(left, 'mac') === canonicalCombo(right, 'mac') ||
    canonicalCombo(left, 'other') === canonicalCombo(right, 'other')
  );
}

type ComboPlatform = 'literal' | 'mac' | 'other';

function canonicalCombo(keys: string, platform: ComboPlatform): string {
  const parts = keys
    .toLowerCase()
    .split('+')
    .map((part) => part.trim())
    .filter(Boolean);

  const modifiers: string[] = [];
  let key = '';

  for (const part of parts) {
    const modifier = modifierFor(part, platform);
    if (modifier) {
      modifiers.push(modifier);
      continue;
    }
    key = part;
  }

  modifiers.sort();
  return [...modifiers, key].join('+');
}

const NAMED_MODIFIERS: Record<string, string> = {
  meta: 'meta',
  cmd: 'meta',
  command: 'meta',
  ctrl: 'ctrl',
  control: 'ctrl',
  alt: 'alt',
  option: 'alt',
  shift: 'shift',
};

const MOD_BY_PLATFORM: Record<ComboPlatform, string> = {
  mac: 'meta',
  other: 'ctrl',
  literal: 'mod',
};

function modifierFor(part: string, platform: ComboPlatform): string | null {
  if (part === 'mod') return MOD_BY_PLATFORM[platform];
  return NAMED_MODIFIERS[part] ?? null;
}

function findConflictingHotkey(
  overrides: HotkeyOverrides,
  id: HotkeyId,
  keys: string,
): HotkeyId | null {
  for (const entry of HOTKEY_REGISTRY) {
    if (entry.id === id) continue;
    if (combosConflict(resolveHotkeyKeys(overrides, entry.id), keys)) {
      return entry.id;
    }
  }
  return null;
}

function clearOverride(
  current: HotkeyOverrides,
  id: HotkeyId,
): HotkeyOverrides {
  if (!(id in current)) return pruneUnknownOverrides(current);
  const next = { ...current };
  delete next[id];
  return pruneUnknownOverrides(next);
}

function pruneUnknownOverrides(overrides: HotkeyOverrides): HotkeyOverrides {
  const next: HotkeyOverrides = {};
  for (const [key, value] of Object.entries(overrides)) {
    if (isHotkeyId(key)) {
      next[key] = value;
    }
  }
  return next;
}
